import { FILE_CHUNK_BYTES, sha256Hex, uuidv7, type PortalView } from '@clim/domain';

const API_URL = import.meta.env.VITE_API_URL ?? '';

export class PortalError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

/** API du portail : le jeton voyage dans un en-tête, jamais dans une URL (ni dans les journaux). */
export function portalApi(token: string) {
  const call = async <T>(method: string, path: string, body?: unknown): Promise<T> => {
    const binary = body instanceof Uint8Array;
    let res: Response;
    try {
      res = await fetch(`${API_URL}/v1/portal${path}`, {
        method,
        headers: {
          'x-portal-token': token,
          ...(body === undefined ? {} : { 'content-type': binary ? 'application/octet-stream' : 'application/json' }),
        },
        body: body === undefined ? undefined : binary ? (body as BodyInit) : JSON.stringify(body),
        credentials: 'omit',
        referrerPolicy: 'no-referrer',
      });
    } catch {
      throw new PortalError(0, 'Connexion impossible : vérifiez votre réseau puis réessayez');
    }
    if (res.status === 204) return undefined as T;
    const json = await res.json().catch(() => null);
    if (!res.ok) throw new PortalError(res.status, json?.error?.message ?? 'Erreur', json?.error?.details);
    return json as T;
  };
  return {
    view: () => call<PortalView>('GET', ''),
    updateClient: (fields: Record<string, string | null>) => call<void>('PATCH', '/client', fields),
    sign: (body: unknown) => call<{ signed: boolean }>('POST', '/signature', body),
    fileUrl: async (id: string) => {
      const r = await call<{ url: string }>('GET', `/files/${id}/url`);
      return r.url.startsWith('/') ? `${API_URL}${r.url}` : r.url;
    },
    /** dépôt d'un fichier : déclaration puis envoi par morceaux (reprise possible) */
    async upload(dossierId: string, f: { kind: 'photo' | 'document'; name: string; mime: string; data: Uint8Array; originalFilename: string }, onProgress?: (p: number) => void) {
      const id = uuidv7();
      await call('POST', '/files', { id, dossierId, kind: f.kind, name: f.name, mime: f.mime, sizeBytes: f.data.byteLength, sha256: await sha256Hex(f.data), originalFilename: f.originalFilename });
      let offset = 0;
      while (offset < f.data.byteLength) {
        const chunk = f.data.subarray(offset, offset + FILE_CHUNK_BYTES);
        try {
          const st = await call<{ receivedBytes: number; status: string }>('PUT', `/files/${id}/content?offset=${offset}`, chunk);
          offset = st.receivedBytes;
        } catch (e) {
          const received = (e as PortalError).details as { receivedBytes?: number } | undefined;
          if (e instanceof PortalError && e.status === 409 && typeof received?.receivedBytes === 'number') offset = received.receivedBytes;
          else throw e;
        }
        onProgress?.(Math.round((offset / f.data.byteLength) * 100));
      }
    },
  };
}
